import { showSuccessAlert } from 'actions/alerts';
import {
  fetchAllRules,
  fetchAllRulesDebounced,
  fetchAllRulesFailure,
  fetchAllRulesRequest,
  fetchAllRulesSuccess,
  fetchAllRulesWithSummary,
  fetchFilterRules,
  fetchRelationshipsSuccess,
  fetchRuleConfigSuccess,
  fetchSingleRule,
  fetchSingleRuleFailure,
  fetchSingleRuleRequest,
  fetchSingleRuleSuccess,
  fetchTestCaseSummaryRule,
  fetchTestCaseSummaryRuleFailure,
  fetchTestCaseSummaryRuleRequest,
  fetchTestCaseSummaryRuleSuccess,
  publishRuleVersion,
  publishRuleVersionFailure,
  publishRuleVersionRequest,
  publishRuleVersionSuccess,
  undoRuleVersion,
  undoRuleVersionFailure,
  undoRuleVersionRequest,
  undoRuleVersionSuccess,
} from 'actions/rule';
import { apiClient as LitLingoClient } from 'client';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { normalizeRuleConfig } from 'reducers/util/flattenConfigData';
import type { Channel } from 'redux-saga';
import { buffers } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  debounce,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { getHasUnsavedChanges } from 'selectors/config';
import { getFiltersSearchString } from 'selectors/envelopes';
import { getNavParamsByResource } from 'selectors/nav';
import { getRule } from 'selectors/rules';
import type { API, MRule, SagaReturn } from 'types';
import { v4 as uuidv4 } from 'uuid';

function* fetchAllRulesSaga({ payload }: ReturnType<typeof fetchAllRules>): SagaReturn {
  yield put(fetchAllRulesRequest());
  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.rule)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;
  const params = {
    ...payload,
    include_count: true,
    ...resourceParams,
    relationships: ['created_by', 'updated_by'],
  };

  const response = (yield call([LitLingoClient.resources.rules, 'list'], {
    params,
  })) as API.Response<API.Rules.List>;
  if (response.error != null) {
    yield put(fetchAllRulesFailure(response.error));
  } else {
    yield put(fetchAllRulesSuccess(response.data));
  }
}

function* fetchAllRulesWithSummarySaga({ payload }: ReturnType<typeof fetchAllRules>): SagaReturn {
  yield put(fetchAllRulesRequest());
  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.rule)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;
  const params = {
    ...payload,
    include_count: true,
    ...resourceParams,
    relationships: ['created_by', 'updated_by'],
  };

  const response = (yield call([LitLingoClient.resources.rules, 'list'], {
    params,
  })) as API.Response<API.Rules.List>;
  if (response.error != null) {
    yield put(fetchAllRulesFailure(response.error));
  } else {
    yield put(fetchAllRulesSuccess(response.data));
    yield all(
      response.data.records.map((rule) => put(fetchTestCaseSummaryRule({ rule_uuids: rule.uuid })))
    );
  }
}

function* fetchFiltersRulesSaga({ payload }: ReturnType<typeof fetchFilterRules>): SagaReturn {
  yield put(fetchAllRulesRequest());
  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.rule)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const filtersSearch = (yield select(getFiltersSearchString)) as ReturnType<
    typeof getFiltersSearchString
  >;

  const params = {
    ...payload,
    include_count: true,
    ...resourceParams,
    selectable_fields: ['name', 'uuid', 'rule_uuid'],
    filters_search: filtersSearch,
  };

  const response = (yield call([LitLingoClient.resources.rules, 'list'], {
    params,
  })) as API.Response<API.Rules.List>;
  if (response.error != null) {
    yield put(fetchAllRulesFailure(response.error));
  } else {
    yield put(fetchAllRulesSuccess(response.data));
  }
}

function* fetchSingleRuleSaga({ payload }: ReturnType<typeof fetchSingleRule>): SagaReturn {
  yield put(fetchSingleRuleRequest());

  const { ruleId } = payload;

  const rule = (yield select(getRule(ruleId))) as MRule;
  const unsavedChanges = (yield select(getHasUnsavedChanges)) as boolean;

  if (rule && unsavedChanges) {
    return;
  }

  const response = (yield call([LitLingoClient.resources.rules, 'retrieve'], ruleId, {
    params: {
      relationships: ['annotators', 'annotators.annotator', 'updated_by'],
      include_pii: true,
    },
  })) as API.Response<API.Rules.Retrieve>;
  if (response.error != null) {
    yield put(fetchSingleRuleFailure(response.error));
  } else {
    const rootConfigId = response.data.config.uuid || uuidv4();
    yield put(fetchSingleRuleSuccess({ ...response.data, rootConfigId }));
    const config = {
      ...response.data.config,
      rootConfigId,
    };
    const { ruleConfig, relationships } = normalizeRuleConfig({
      config,
      rule: response.data,
    });

    yield put(fetchRuleConfigSuccess(ruleConfig));
    yield put(fetchRelationshipsSuccess(relationships));
  }
}

function* fetchTestCasesSummaryRuleSaga({
  payload,
}: ReturnType<typeof fetchTestCaseSummaryRule>): SagaReturn {
  const { rule_uuids: ruleUuid } = payload;

  yield put(fetchTestCaseSummaryRuleRequest());

  const response = (yield call([LitLingoClient.resources.testCases.extras, 'summary'], {
    params: payload,
  })) as API.Response<API.Tests.Summary>;

  if (response.error != null) {
    yield put(fetchTestCaseSummaryRuleFailure(response.error));
  } else {
    const data = {
      ...response.data,
      ruleUuid,
    };
    yield put(fetchTestCaseSummaryRuleSuccess(data));
  }
}

function* publishRuleVersionSaga({ payload }: ReturnType<typeof publishRuleVersion>): SagaReturn {
  const { ruleId, version } = payload;

  yield put(publishRuleVersionRequest());

  const response = (yield call([LitLingoClient.resources.rules.extras, 'publishVersion'], {
    params: {
      relationships: ['annotators', 'annotators.annotator', 'updated_by'],
      include_pii: true,
    },
    urlParams: { ruleId, version },
  })) as API.Response<API.Rules.PublishVersion>;

  if (response.error != null) {
    yield put(publishRuleVersionFailure(response.error));
  } else {
    const rootConfigId = response.data.config.uuid || uuidv4();
    yield put(publishRuleVersionSuccess({ ...response.data, rootConfigId }));

    const config = {
      ...response.data.config,
      rootConfigId,
    };
    const { ruleConfig, relationships } = normalizeRuleConfig({
      config,
      rule: response.data,
    });

    yield put(fetchRuleConfigSuccess(ruleConfig));
    yield put(fetchRelationshipsSuccess(relationships));
    yield put(showSuccessAlert('Version published.'));
  }
}

function* undoRuleVersionSaga({ payload }: ReturnType<typeof undoRuleVersion>): SagaReturn {
  const { ruleId } = payload;

  yield put(undoRuleVersionRequest());

  const response = (yield call([LitLingoClient.resources.rules.extras, 'undoVersion'], {
    urlParams: { ruleId },
    params: {
      relationships: ['annotators', 'annotators.annotator', 'updated_by'],
      include_pii: true,
    },
  })) as API.Response<API.Rules.UndoVersion>;

  if (response.error != null) {
    yield put(undoRuleVersionFailure(response.error));
  } else {
    const rootConfigId = response.data.config.uuid || uuidv4();
    yield put(undoRuleVersionSuccess({ ...response.data, rootConfigId }));

    const config = {
      ...response.data.config,
      rootConfigId,
    };
    const { ruleConfig, relationships } = normalizeRuleConfig({
      config,
      rule: response.data,
    });

    yield put(fetchRuleConfigSuccess(ruleConfig));
    yield put(fetchRelationshipsSuccess(relationships));

    yield put(showSuccessAlert('Model restored to last version.'));
  }
}

function* channels(): SagaReturn {
  const fetchAllRulesChan = (yield actionChannel(
    fetchAllRulesDebounced.toString(),
    buffers.sliding(1)
  )) as Channel<ReturnType<typeof fetchAllRulesDebounced>>;

  yield debounce(400, fetchAllRulesChan, fetchAllRulesSaga);
}

function* rulesSaga(): SagaReturn {
  yield takeEvery(fetchSingleRule, fetchSingleRuleSaga);
  yield takeLatest(fetchAllRules.toString(), fetchAllRulesSaga);
  yield takeEvery(fetchTestCaseSummaryRule.toString(), fetchTestCasesSummaryRuleSaga);
  yield takeLatest(publishRuleVersion.toString(), publishRuleVersionSaga);
  yield takeLatest(undoRuleVersion.toString(), undoRuleVersionSaga);
  yield takeLatest(fetchFilterRules.toString(), fetchFiltersRulesSaga);
  yield takeLatest(fetchAllRulesWithSummary.toString(), fetchAllRulesWithSummarySaga);
  // @ts-expect-error check types
  yield channels();
}

export default rulesSaga;
